import { Middleware } from "@reduxjs/toolkit";
import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { GetExpenseContextResponse } from "redmond";
import { PATH_EXPENSE_CONTEXT } from "../constants/urlPaths";
import {
  buildProjectsAndCategoriesLookups,
  ProjectsAndCategoriesLookups,
} from "../utils/buildProjectsAndCategoriesLookups";
import { unzipProjectsAndCategories } from "../utils/unzipProjectsAndCategories";

const toRtkError = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    return {
      error: {
        status: error.response?.status,
        data: error.response?.data,
      },
    };
  }
  return { error: { status: "UNKNOWN_ERROR", data: undefined } };
};

// Keyed on accountReferenceId: RTK Query caches one entry per account, so
// switching the active account naturally triggers a refetch for the new key
// while the previous account's lookups stay cached in memory for the session.
// Tysons derives accountReferenceId/profileReferenceId from the authenticated
// session itself, not from the request — accountReferenceId here is only the
// RTK Query cache key, it is not sent over the wire.
export const projectsAndCategoriesApi = createApi({
  reducerPath: "projectsAndCategories",
  baseQuery: fakeBaseQuery(),
  tagTypes: [],
  endpoints: (builder) => ({
    getProjectsAndCategories: builder.query<
      ProjectsAndCategoriesLookups,
      { accountReferenceId: string }
    >({
      queryFn: async () => {
        try {
          const { data } = await axios.get<GetExpenseContextResponse>(
            PATH_EXPENSE_CONTEXT
          );
          const payload = unzipProjectsAndCategories(
            data.projectsAndCategoriesPayload
          );
          return { data: buildProjectsAndCategoriesLookups(payload) };
        } catch (error: unknown) {
          return toRtkError(error);
        }
      },
    }),
  }),
});

export const projectsAndCategoriesApiMiddleware: Middleware =
  projectsAndCategoriesApi.middleware;

export const { useGetProjectsAndCategoriesQuery } = projectsAndCategoriesApi;
